import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { DBViewFiltersThunks, isTempId, viewFiltersSelectors } from "@common-reducers/DBServiceThunks";
import { getColumnList } from '@common-reducers/TaskSelectors';
import { addNewfilterConditionThunk, getFilterConfigItemByColumnType, getFilterConfigItemByColumnTypeAndSelectedOperator, getFilterDataByFilterColumnIdForFilterRow, valuesListSelector } from "@common-reducers/TasksFilterPanelThunks";
import { NullableId } from "@common/types/interface";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { namespace } from '@common/config/constants';
import { X } from "lucide-react";
import { memo, useCallback, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useSelector } from "react-redux";
import { ViewFilter } from "./advanced_filters_shadcn";
import { FilterRowColumnFieldShadcn } from './filter_row_column_field_shadcn';
import { FilterRowOperatorFieldShadcn } from './filter_row_operator_field_shadcn';
import FilterRowValueFieldShadcn from './filter_row_value_field_shadcn';
import { usePortalContainer } from "@/common/hooks/use-portal-container";

type AdvancedFilterRowProps = {
    viewFilterId: NullableId;
    isFirstFilter: boolean;
    viewId: any;
};

export const AdvancedFilterRowShadcn = ({ viewFilterId, isFirstFilter, viewId }: AdvancedFilterRowProps) => {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const localPortalContainer = usePortalContainer();
    const filter = useSelector((state: import('@common/types/common-root-state-type').CommonRootState) => viewFiltersSelectors.selectById(state, (viewFilterId ?? '') as string)) as ViewFilter | undefined;
    const filterData = useAppSelector(state => getFilterDataByFilterColumnIdForFilterRow(state, filter?.filterColumnId ?? ''));
    const [andOrOperator, setAndOrOperator] = useState<string>(viewId ? (filter?.filterAndOr ?? '') : 'and');
    const [columnField, setColumnField] = useState<string>(viewId ? (filterData?.columnTitle ?? '') : "");
    const [operatorField, setOperatorField] = useState<string>(viewId ? (filter?.filterOperator ?? '') : "");
    const [valueField, setValueField] = useState<string>(viewId ? (filter?.filterValue ?? '') : "");
    const [, setPersonFilterValue] = useState<string>();
    const [selectedColumnId, setSelectedColumnId] = useState<string>(filterData.columnId ?? '');
    const [selectedColumnType, setSelectedColumnType] = useState<string>(filterData.columnType ?? '');
    const valueFilterOptionList = useAppSelector((state) => valuesListSelector(state, selectedColumnType, selectedColumnId, operatorField));
    const selectedFilterItem = useAppSelector((state) => getFilterConfigItemByColumnType(state, selectedColumnType));
    const columnList = useAppSelector(getColumnList);
    const selectedFilterItemOperator = useAppSelector((state) => getFilterConfigItemByColumnTypeAndSelectedOperator(state, selectedColumnType, operatorField));
    
    // Reset state logic
    const resetState = useCallback(() => {
        setAndOrOperator(isFirstFilter ? '' : 'and');
        setColumnField('');
        setOperatorField('');
        setValueField('');
        setSelectedColumnId('');
        setSelectedColumnType('');
    }, [isFirstFilter]);


    const andOrOperatorChange = (andOrOperatorValue: string) => {
        setAndOrOperator(andOrOperatorValue);
        if (filter) {
            UpdateRow({ filterAndOr: andOrOperatorValue });
        }
    };

    const handleColumnChange = (columnItemId: string) => {
        setValueField('');
        if (columnItemId === 'groupColumn') {
            setSelectedColumnType('group');
            setSelectedColumnId('groupColumn');
            setColumnField('Group');
        } else {
            const selectedColumn = columnList.find(column => columnItemId === column._id);
            setSelectedColumnId(selectedColumn?._id ?? '');
            setSelectedColumnType(selectedColumn?.type ?? '');
            setColumnField(selectedColumn?.title ?? '');
        }
        UpdateRow({ filterColumnId: columnItemId, filterValue: '' });
    };

    const handleOperatorChange = (value: string) => {
        setOperatorField(value);
        const isEmptyOrNotEmptyOperator: boolean = (value === 'is empty' || value === 'is not empty');
        const isCheckboxColumn = selectedColumnType === "checkbox" && (value === "is checked" || value === "is not checked");

        if (isCheckboxColumn) {
            if (filter) {
                UpdateRow({ filterOperator: value, filterValue: value === 'is checked' ? 'Checked' : '' });
            } else {
                addNewFilterRow({ filterOperator: value, filterValue: value === 'is checked' ? 'Checked' : '' });
            }
        } else if (isEmptyOrNotEmptyOperator) {
            if (filter) {
                UpdateRow({ filterOperator: value, filterValue: '' });
            }
            else {
                addNewFilterRow({ filterOperator: value, filterValue: '' });
            }

        } else {
            if (filter) {
                if (value === 'contains' || value === 'does not contain') {
                    setValueField('');
                    UpdateRow({ filterOperator: value, filterValue: '' });
                } else {
                    UpdateRow({ filterOperator: value });
                }
            } else {
                addNewFilterRow({ filterOperator: value, filterValue: '' });
            }
        }
    };

    const handleChangeValueField = (value: any) => {
        if (value === 'is checked') {
            setValueField('Checked');
        } else if (value === 'is not checked') {
            setValueField('blank');
        }

        const selectedPerson = valueFilterOptionList.find(personObject => (personObject as { id?: string })?.id === valueField) as { id?: string; value?: string } | undefined;
        if (selectedPerson) {
            setPersonFilterValue(selectedPerson.value);
        }
        setValueField(value);
        if (filter) {
            UpdateRow({ filterValue: value });
        } else {
            addNewFilterRow({ filterValue: value });
        }
    };

    const addNewFilterRow = useCallback(async (filterCondition: ViewFilter) => {
        const operatorFieldIsEmptyOrNotEmpty = filterCondition.filterOperator === 'is empty' || filterCondition.filterOperator === 'is not empty';
        if (selectedColumnId && (filterCondition.filterOperator || operatorField) && (operatorFieldIsEmptyOrNotEmpty || (valueField || filterCondition.filterValue))) {
            const clonedFilterCondition = {
                filterColumnId: selectedColumnId,
                filterAndOr: andOrOperator,
                filterOperator: filterCondition.filterOperator || operatorField,
                filterValue: filterCondition.filterValue || valueField
            };
            resetState();
            await dispatch(addNewfilterConditionThunk(clonedFilterCondition));
        }
    }, [selectedColumnId, operatorField, valueField, andOrOperator, resetState, dispatch]);

    const UpdateRow = useCallback(async (filterCondition: ViewFilter) => {
        if (isTempId(filter?._id ?? null)) {
            return;
        }
        const patchEntity = {
            entity: {
                _id: filter?._id,
                ...filterCondition,
            },
        };
        await dispatch(DBViewFiltersThunks.patch(patchEntity));
    }, [dispatch, filter?._id]);

    function deleteFilterRow(filterId: string) {
        dispatch(DBViewFiltersThunks.delete({ entity: { _id: filterId } }));
        setColumnField('');
        setValueField('');
    }

    return (
        <div className="w-full">
            <div className="grid grid-cols-[0.5fr_2fr_2fr_3fr_0.5fr] gap-2 items-center w-full min-h-9">
                {/* 1. And/Or Label or Select */}
                {isFirstFilter ? (
                     <div className="flex items-center text-sm font-medium pl-1 text-muted-foreground w-[70px]">
                        {t('advancedFilterRow.where')}
                     </div>
                ) : (
                    <div className="w-[70px]">
                         <Select value={andOrOperator ?? ''} onValueChange={andOrOperatorChange}>
                            <SelectTrigger className="w-full h-9">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="and">And</SelectItem>
                                <SelectItem value="or">Or</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                )}
                
                {/* 2. Column Select */}
                <div className="w-full">
                     <FilterRowColumnFieldShadcn
                        selectedColumnId={selectedColumnId} // Use selectedColumnId for binding
                        onColumnChange={handleColumnChange}
                     />
                </div>

                {/* 3. Operator Select */}
                <div className="w-full">
                     <FilterRowOperatorFieldShadcn
                        operatorField={operatorField}
                        onOperatorChange={handleOperatorChange}
                        selectedColumnType={selectedColumnType}
                     />
                </div>

                {/* 4. Value Input */}
                <div className="w-full">
                    {(columnField && !selectedFilterItemOperator?.isHideValueField && !(selectedFilterItem?.isHideValueField)) ? (
                        <FilterRowValueFieldShadcn
                            isDisableTempId={isTempId(filter?._id ?? null)}
                            selectedColumnType={selectedColumnType}
                            selectedColumnId={selectedColumnId}
                            operatorField={operatorField}
                            valueField={valueField ?? ''}
                            handleChangeValueField={handleChangeValueField}
                        />
                    ) : (
                        <div className="w-full"></div>
                    )}
                </div>

                {/* 5. Delete Button */}
                <div className="flex justify-end">
                    {filter && (
                         <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 rounded-full text-muted-foreground hover:text-destructive"
                            onClick={() => deleteFilterRow(filter._id!)}
                         >
                            <X className="h-4 w-4" />
                        </Button>
                    )}
                </div>

            </div>
        </div>
    );
};

export default memo(AdvancedFilterRowShadcn);
